// The form-fields editor's lists and sentences, as pure functions.
//
// A pure module rather than logic inside the component, for this repository's standing
// reason: there is no DOM harness, so anything decidable without a DOM is decided here
// and asserted by test/web/form-fields.test.js — the shape descriptor-form.js,
// on-shift.js and queue-members.js established.
//
// THE THING THIS SCREEN DECIDES is what an agent meets on /tickets/new with a customer
// on the phone: which questions get asked, in what order, and which cannot be skipped.
// Every failure mode is silent. A field dropped from a form stops being collected on
// every ticket from that moment, and nothing errors. A field moved to the bottom is one
// an agent stops reaching. A required flag turned off is why a month of tickets is
// missing a serial number.

/**
 * The fields NOT yet on this form, ready to be added.
 *
 * Retired definitions are excluded from the ADD list but not from the form's own list
 * (see `describeField`): attaching a field the team has retired asks an agent mid-call
 * to fill in something nobody wants any more, while a retired field already attached is
 * a fact the admin needs to see in order to remove it.
 *
 * @param {Array} all       `GET /api/admin/custom-fields`
 * @param {Array} attached  `GET /api/admin/ticket-forms/:id/fields`, in order
 */
export function availableFields(all, attached) {
  const taken = new Set((attached ?? []).map((f) => String(f.id)));
  return (all ?? [])
    .filter((f) => !taken.has(String(f.id)))
    .filter((f) => f.is_active !== false)
    .sort((a, b) => String(a.label ?? '').localeCompare(String(b.label ?? '')));
}

/** Move the entry at `index` by `delta`, or return the list unchanged at the ends. */
export function move(fields, index, delta) {
  const list = [...(fields ?? [])];
  const target = index + delta;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return list;
  [list[index], list[target]] = [list[target], list[index]];
  return list;
}

/** Append one field, or return the list unchanged when it is already on the form. */
export function addField(fields, field) {
  const list = [...(fields ?? [])];
  if (!field?.id) return list;
  if (list.some((f) => String(f.id) === String(field.id))) return list;
  // `is_required` starts FALSE regardless of the definition's own flag. The definition's
  // is a suggestion for new forms; this one blocks submission on this form, and turning
  // it on should be a decision somebody made on this screen.
  list.push({ ...field, is_required: false });
  return list;
}

export function removeField(fields, id) {
  return (fields ?? []).filter((f) => String(f.id) !== String(id));
}

export function setRequired(fields, id, required) {
  return (fields ?? []).map((f) => (
    String(f.id) === String(id) ? { ...f, is_required: required === true } : f
  ));
}

/**
 * The PUT body: ids and required flags, in order.
 *
 * `sort_order` is deliberately absent — the server assigns it from this array's index,
 * so two fields can never share a position. Sending our own numbers would reintroduce
 * exactly that.
 */
export function fieldsPayload(fields) {
  return (fields ?? []).map((f) => ({ id: String(f.id), is_required: f.is_required === true }));
}

/** Whether anything differs from what was loaded — order included. */
export function hasChanges(saved, draft) {
  return JSON.stringify(fieldsPayload(saved)) !== JSON.stringify(fieldsPayload(draft));
}

/**
 * What to say about one attached field. TOTALITY, the property on-shift.js states:
 * every input gets a non-empty sentence, because a blank cell reads as "fine".
 */
export function describeField(field) {
  const row = field ?? {};
  const type = row.data_type ? String(row.data_type) : 'text';

  if (row.is_active === false) {
    return {
      ok: false,
      // The one case where the form and the create screen disagree, so it has to say
      // which one the agent sees. `listTicketForms` filters inactive definitions out.
      text: 'This field is retired, so the create screen does not render it. '
        + 'It is still attached here until you remove it.',
    };
  }
  if (row.is_required) {
    return { ok: true, text: `Required on this form. Collected as ${type}.` };
  }
  return { ok: true, text: `Optional on this form. Collected as ${type}.` };
}

/**
 * The one-line summary above the list.
 *
 * A form that collects nothing is legitimate and is also the state an admin passes
 * through while rebuilding one, so it reads as a fact rather than as an error.
 */
export function summarise(fields) {
  const list = fields ?? [];
  if (list.length === 0) {
    return 'This form asks for nothing beyond the subject and description.';
  }
  const required = list.filter((f) => f.is_required).length;
  const noun = list.length === 1 ? 'field' : 'fields';
  if (required === 0) return `${list.length} ${noun}, none of them required.`;
  return `${list.length} ${noun}, ${required} required.`;
}
